/**
 * i18n configuration for the Catalogue PWA.
 * Supports English (EN), French (FR) and Spanish (ES).
 * Language is persisted in localStorage under 'catalogue-lang'.
 * Import this file once in main.tsx (side-effect: initialises i18next).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export type SupportedLang = 'en' | 'fr' | 'es';
export const SUPPORTED_LANGS: { code: SupportedLang; label: string }[] = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
];

const en = {
  // ── Login ─────────────────────────────────────────────────────────────
  'login.title': 'Product Catalogue',
  'login.subtitle': 'Sign in with your Trac-IT credentials',
  'login.username': 'Username',
  'login.password': 'Password',
  'login.signin': 'Sign In',
  'login.error': 'Login failed. Please check your credentials.',

  // ── Header ────────────────────────────────────────────────────────────
  'header.menu': 'Menu',
  'header.syncing': 'Syncing…',
  'header.synced': 'Synced {{time}}',
  'header.not_synced': 'Not synced',
  'header.sync_tooltip': 'Sync data and refresh product photos',
  'header.sync_offline': 'Offline — cannot sync',
  'header.offline': 'Offline',
  'header.settings': 'Settings',
  'header.signout': 'Sign out',
  'install.button': 'Install app',
  'install.tooltip': 'Install this catalogue as a desktop app',
  'install.help_title': 'Install the app',
  'install.help_intro': "If the one-click prompt didn't appear, add the app in two clicks:",
  'install.help_edge_label': 'Microsoft Edge',
  'install.help_edge': 'Open the ⋯ menu → More tools → Apps → “Install Didier Catalogue”. (Or click the install icon at the right of the address bar.)',
  'install.help_chrome_label': 'Google Chrome',
  'install.help_chrome': 'Click the install icon at the right of the address bar, then “Install”. (Or: ⋮ menu → Cast, save and share → Install page as app.)',
  'install.help_installed_note': 'Already installed? Open it from the ⋯ menu → “Open in Didier Catalogue”, or from the Windows Start menu.',
  'install.help_close': 'Got it',

  // ── Catalogue toolbar ─────────────────────────────────────────────────
  'catalogue.search_placeholder': 'Search code / description / category…',
  'catalogue.live_only': 'Live only',
  'catalogue.select_all': 'Select all visible products',
  'catalogue.deselect_all': 'Deselect all',
  'catalogue.n_selected': '{{count}} selected',
  'catalogue.none_selected': 'None selected',
  'catalogue.pdf_button': 'PDF ({{count}})',
  'catalogue.generating': 'Generating…',
  'catalogue.lookbook_button': 'Open Lookbook ({{count}})',
  'catalogue.n_products': '{{count}} products',
  'catalogue.suitability_label': 'Suitability:',
  'catalogue.allergens_label': 'Allergens:',
  'catalogue.clear': 'Clear',
  'catalogue.clear_all': 'Clear all',
  'catalogue.no_prefix': 'No ',
  'catalogue.airline_mode': 'Airline mode',
  'catalogue.show_ingredients': 'Show ingredients',
  'catalogue.show_prices': 'Show prices',
  'catalogue.show_defrost_life': 'Show defrosted shelf life',
  'catalogue.show_handling': 'Show handling',
  'catalogue.filter_to_selected': 'Filter to selected',
  'catalogue.show_only_selected': 'Show only selected products in grid',
  'catalogue.show_all_products': 'Show all products in grid',

  // ── Selected products drawer ───────────────────────────────────────────
  'drawer.n_selected_one': '{{count}} product selected',
  'drawer.n_selected_other': '{{count}} products selected',
  'drawer.no_selected': 'No products selected',
  'drawer.remove_all': 'Remove All',
  'drawer.empty_hint': 'Check products in the grid to add them here.',
  'drawer.move_up': 'Move up',
  'drawer.move_down': 'Move down',
  'drawer.remove': 'Remove from selection',
  'drawer.generate_pdf': 'Generate PDF ({{count}})',
  'drawer.lookbook': 'Lookbook',

  // ── Preview panel ─────────────────────────────────────────────────────
  'preview.case_size': 'Case Size: x{{n}}',
  'preview.weight': 'Weight: {{n}} g',
  'preview.weight_per_unit': 'Weight: {{n}} g per unit',
  'preview.fp_category': 'FP Category:',
  'preview.web_category': 'Web Category:',
  'preview.price': 'Price: £{{n}}',
  'preview.defrost_life': 'Defrosted shelf life: {{n}} days',
  'preview.handling': 'Handling:',
  'preview.suitable_for': 'Suitable for',
  'preview.allergens': 'Allergens',
  'preview.ingredients': 'Ingredients',
  'preview.allergen_decl': 'Allergen Declaration',
  'preview.contains': 'Contains: ',
  'preview.no_marketing_desc': 'No marketing description',
  'preview.personalized': 'Personalized',
  'preview.reset_personalization': 'Reset to catalogue',
  'preview.save': 'Save',
  'preview.cancel': 'Cancel',
  'preview.saving': 'Saving…',
  'preview.tier': 'Tier',

  // ── PDF confirm dialog ────────────────────────────────────────────────
  'pdfconfirm.title': 'Generate PDF Catalogue',
  'pdfconfirm.and_more': '…and {{n}} more',
  'pdfconfirm.settings': 'Settings:',
  'pdfconfirm.chip_airline': 'Airline mode',
  'pdfconfirm.chip_prices': 'Show prices',
  'pdfconfirm.chip_ingredients': 'Show ingredients',
  'pdfconfirm.chip_defrost': 'Defrosted shelf life',
  'pdfconfirm.chip_handling': 'Handling',
  'pdfconfirm.standard': 'Standard (no extras)',
  'pdfconfirm.cancel': 'Cancel',
  'pdfconfirm.generate': 'Generate PDF',

  // ── PDF document labels ───────────────────────────────────────────────
  'pdf.suitable_for': 'Suitable for:',
  'pdf.allergens': 'Allergens:',
  'pdf.ingredients': 'Ingredients:',
  'pdf.defrost_life': 'Defrosted shelf life:',
  'pdf.handling': 'Handling:',
  'pdf.case_size': 'Case Size:',
  'pdf.weight': 'Weight:',
  'pdf.price': 'Price:',
  'pdf.per_unit': 'per unit',
  'pdf.days': 'days',
  'pdf.product_catalogue': 'Product Catalogue',
  'pdf.print_button': 'Print / Save PDF',
  'pdf.close_button': 'Close',

  // ── Allergen labels (display) ─────────────────────────────────────────
  'allergen.Gluten': 'Gluten',
  'allergen.Egg': 'Egg',
  'allergen.Nuts': 'Nuts',
  'allergen.Soya': 'Soya',
  'allergen.Dairy': 'Dairy',
  'allergen.Sesame': 'Sesame',
  'allergen.Sulphites': 'Sulphites',
  'allergen.Fish': 'Fish',
  'allergen.Celery': 'Celery',
  'allergen.Mustard': 'Mustard',
  'allergen.Lupin': 'Lupin',
  'allergen.Mollusc': 'Mollusc',

  // ── Suitability labels (display) ──────────────────────────────────────
  'suitability.Halal': 'Halal',
  'suitability.Vegetarian': 'Vegetarian',
  'suitability.Vegan': 'Vegan',

  // ── Airline tier labels ───────────────────────────────────────────────
  'tier.business': 'Business',
  'tier.first': 'First',
  'tier.economy': 'Economy',

  // ── Lookbook ──────────────────────────────────────────────────────────
  'lookbook.no_products': 'No products in presentation set',
  'lookbook.no_products_hint': 'Select products on the catalogue page, then launch the Lookbook.',
  'lookbook.back_to_catalogue': 'Back to catalogue',
  'lookbook.back': 'Back',
  'lookbook.counter': '{{current}} of {{total}}',
  'lookbook.portfolios': 'Portfolios',
  'lookbook.customer_pdf': 'Customer PDF ({{count}})',
  'lookbook.no_image': 'No image',
  'lookbook.in_selection': 'In my selection',
  'lookbook.add_to_selection': 'Add to my selection',
  'lookbook.my_portfolios': 'My Portfolios',
  'lookbook.portfolio_name_placeholder': 'Portfolio name…',
  'lookbook.no_portfolios': 'No saved portfolios yet',

  // ── Guided tour ───────────────────────────────────────────────────────
  'tour.btn.next': 'Next',
  'tour.btn.back': 'Back',
  'tour.btn.skip': 'Skip',
  'tour.btn.last': 'Got it',
  'tour.btn.close': 'Close',
  'tour.welcome.title': 'Welcome to the Product Catalogue',
  'tour.welcome.body': 'Quick tour of what you can do here. It takes about a minute — you can replay it anytime from the “?” button.',
  'tour.lang.title': 'Choose your language',
  'tour.lang.body': 'Switch the whole app between English, French and Spanish. Product descriptions and PDFs follow your choice.',
  'tour.search.title': 'Search products',
  'tour.search.body': 'Find products by code, description or category. Results filter as you type.',
  'tour.live.title': 'Live products only',
  'tour.live.body': 'Keep this on to show only products currently available. Turn it off to see everything.',
  'tour.filters.title': 'Filter by suitability & allergens',
  'tour.filters.body': 'Tap a chip once to show only matching products, tap again to exclude them. Great for dietary requests.',
  'tour.toggles.title': 'Show more detail',
  'tour.toggles.body': 'Turn these on to add ingredients, prices, defrosted shelf life and handling guidance to the preview, PDF and Lookbook.',
  'tour.grid.title': 'Browse & select',
  'tour.grid.body': 'Click a row to preview a product. Tick the box to add it to your selection. Double-click a cell to edit text inline.',
  'tour.selection.title': 'Your selection',
  'tour.selection.body': 'This shows how many products you have selected. Click it to reorder, remove, or review them before exporting.',
  'tour.pdf.title': 'Export a PDF',
  'tour.pdf.body': 'Generate a print-ready catalogue PDF of your selected products, in the current language.',
  'tour.lookbook.title': 'Open the Lookbook',
  'tour.lookbook.body': 'Launch a full-screen, photo-led presentation of your selection — ideal for showing customers.',
  'tour.preview.title': 'Preview & edit',
  'tour.preview.body': 'The preview panel shows full product detail. When online you can edit the marketing title and description right here.',
  'tour.sync.title': 'Sync',
  'tour.sync.body': 'Refresh product data and photos. The app also works offline using the last synced data.',
  'tour.help.title': 'Replay this tour anytime',
  'tour.help.body': 'Forgot how something works? Click this “?” button to run the tour again.',
  'tour.done.title': 'You’re all set',
  'tour.done.body': 'That’s the essentials. Explore at your own pace — and replay the tour from “?” whenever you need it.',
  'tour.lb.welcome.title': 'Welcome to the Lookbook',
  'tour.lb.welcome.body': 'A full-screen presentation of your selected products. Here’s how to use it.',
  'tour.lb.nav.title': 'Move between products',
  'tour.lb.nav.body': 'Use these arrows — or the ← → keys on your keyboard — to move through your selection.',
  'tour.lb.add.title': 'Build a customer selection',
  'tour.lb.add.body': 'Add the products a customer likes to “my selection”, then export them as a tailored PDF.',
  'tour.lb.portfolios.title': 'Portfolios',
  'tour.lb.portfolios.body': 'Save a selection as a named portfolio to reopen later — handy per customer or per season.',
  'tour.lb.pdf.title': 'Customer PDF',
  'tour.lb.pdf.body': 'Export your chosen products as a clean, branded PDF to send to the customer.',
  'tour.lb.done.title': 'Enjoy the Lookbook',
  'tour.lb.done.body': 'That’s it — present, select and export with ease.',
};

const fr: typeof en = {
  'login.title': 'Catalogue Produits',
  'login.subtitle': 'Connectez-vous avec vos identifiants Trac-IT',
  'login.username': 'Identifiant',
  'login.password': 'Mot de passe',
  'login.signin': 'Se connecter',
  'login.error': 'Échec de connexion. Vérifiez vos identifiants.',

  'header.menu': 'Menu',
  'header.syncing': 'Synchronisation…',
  'header.synced': 'Synchronisé à {{time}}',
  'header.not_synced': 'Non synchronisé',
  'header.sync_tooltip': 'Synchroniser les données et actualiser les photos',
  'header.sync_offline': 'Hors ligne — synchronisation impossible',
  'header.offline': 'Hors ligne',
  'header.settings': 'Paramètres',
  'header.signout': 'Se déconnecter',
  'install.button': 'Installer l’app',
  'install.tooltip': 'Installer ce catalogue comme application de bureau',
  'install.help_title': 'Installer l’application',
  'install.help_intro': "Si l’installation en un clic ne s’est pas affichée, ajoutez l’app en deux clics :",
  'install.help_edge_label': 'Microsoft Edge',
  'install.help_edge': 'Ouvrez le menu ⋯ → Plus d’outils → Applications → « Installer Didier Catalogue ». (Ou cliquez sur l’icône d’installation à droite de la barre d’adresse.)',
  'install.help_chrome_label': 'Google Chrome',
  'install.help_chrome': 'Cliquez sur l’icône d’installation à droite de la barre d’adresse, puis « Installer ». (Ou : menu ⋮ → Diffuser, enregistrer et partager → Installer la page en tant qu’application.)',
  'install.help_installed_note': 'Déjà installée ? Ouvrez-la depuis le menu ⋯ → « Ouvrir dans Didier Catalogue », ou depuis le menu Démarrer de Windows.',
  'install.help_close': 'Compris',

  'catalogue.search_placeholder': 'Recherche code / description / catégorie…',
  'catalogue.live_only': 'Actifs seulement',
  'catalogue.select_all': 'Sélectionner tous les produits visibles',
  'catalogue.deselect_all': 'Tout désélectionner',
  'catalogue.n_selected': '{{count}} sélectionné(s)',
  'catalogue.none_selected': 'Aucune sélection',
  'catalogue.pdf_button': 'PDF ({{count}})',
  'catalogue.generating': 'Génération…',
  'catalogue.lookbook_button': 'Ouvrir le Lookbook ({{count}})',
  'catalogue.n_products': '{{count}} produits',
  'catalogue.suitability_label': 'Convient à :',
  'catalogue.allergens_label': 'Allergènes :',
  'catalogue.clear': 'Effacer',
  'catalogue.clear_all': 'Tout effacer',
  'catalogue.no_prefix': 'Sans ',
  'catalogue.airline_mode': 'Mode aviation',
  'catalogue.show_ingredients': 'Afficher les ingrédients',
  'catalogue.show_prices': 'Afficher les prix',
  'catalogue.show_defrost_life': 'Afficher la durée de vie décongelée',
  'catalogue.show_handling': 'Afficher la manipulation',
  'catalogue.filter_to_selected': 'Filtrer la sélection',
  'catalogue.show_only_selected': 'Afficher uniquement les produits sélectionnés',
  'catalogue.show_all_products': 'Afficher tous les produits',

  'drawer.n_selected_one': '{{count}} produit sélectionné',
  'drawer.n_selected_other': '{{count}} produits sélectionnés',
  'drawer.no_selected': 'Aucun produit sélectionné',
  'drawer.remove_all': 'Tout supprimer',
  'drawer.empty_hint': 'Cochez des produits dans le tableau pour les ajouter ici.',
  'drawer.move_up': 'Monter',
  'drawer.move_down': 'Descendre',
  'drawer.remove': 'Retirer de la sélection',
  'drawer.generate_pdf': 'Générer le PDF ({{count}})',
  'drawer.lookbook': 'Lookbook',

  'preview.case_size': 'Taille caisse : x{{n}}',
  'preview.weight': 'Poids : {{n}} g',
  'preview.weight_per_unit': 'Poids : {{n}} g par unité',
  'preview.fp_category': 'Catégorie PF :',
  'preview.web_category': 'Catégorie Web :',
  'preview.price': 'Prix : £{{n}}',
  'preview.defrost_life': 'Durée de vie décongelée : {{n}} jours',
  'preview.handling': 'Manipulation :',
  'preview.suitable_for': 'Convient à',
  'preview.allergens': 'Allergènes',
  'preview.ingredients': 'Ingrédients',
  'preview.allergen_decl': 'Déclaration allergènes',
  'preview.contains': 'Contient : ',
  'preview.no_marketing_desc': 'Pas de description marketing',
  'preview.personalized': 'Personnalisé',
  'preview.reset_personalization': 'Réinitialiser au catalogue',
  'preview.save': 'Enregistrer',
  'preview.cancel': 'Annuler',
  'preview.saving': 'Enregistrement…',
  'preview.tier': 'Classe',

  'pdfconfirm.title': 'Générer le catalogue PDF',
  'pdfconfirm.and_more': '…et {{n}} de plus',
  'pdfconfirm.settings': 'Paramètres :',
  'pdfconfirm.chip_airline': 'Mode aviation',
  'pdfconfirm.chip_prices': 'Afficher les prix',
  'pdfconfirm.chip_ingredients': 'Afficher les ingrédients',
  'pdfconfirm.chip_defrost': 'Durée de vie décongelée',
  'pdfconfirm.chip_handling': 'Manipulation',
  'pdfconfirm.standard': 'Standard (sans options)',
  'pdfconfirm.cancel': 'Annuler',
  'pdfconfirm.generate': 'Générer le PDF',

  'pdf.suitable_for': 'Convient à :',
  'pdf.allergens': 'Allergènes :',
  'pdf.ingredients': 'Ingrédients :',
  'pdf.defrost_life': 'Durée de vie décongelée :',
  'pdf.handling': 'Manipulation :',
  'pdf.case_size': 'Taille caisse :',
  'pdf.weight': 'Poids :',
  'pdf.price': 'Prix :',
  'pdf.per_unit': 'par unité',
  'pdf.days': 'jours',
  'pdf.product_catalogue': 'Catalogue Produits',
  'pdf.print_button': 'Imprimer / Enregistrer en PDF',
  'pdf.close_button': 'Fermer',

  'allergen.Gluten': 'Gluten',
  'allergen.Egg': 'Œufs',
  'allergen.Nuts': 'Fruits à coque',
  'allergen.Soya': 'Soja',
  'allergen.Dairy': 'Lait',
  'allergen.Sesame': 'Sésame',
  'allergen.Sulphites': 'Anhydrides sulfureux',
  'allergen.Fish': 'Poissons',
  'allergen.Celery': 'Céleri',
  'allergen.Mustard': 'Moutarde',
  'allergen.Lupin': 'Lupin',
  'allergen.Mollusc': 'Mollusques',

  'suitability.Halal': 'Halal',
  'suitability.Vegetarian': 'Végétarien',
  'suitability.Vegan': 'Végétalien',

  'tier.business': 'Business',
  'tier.first': 'Première classe',
  'tier.economy': 'Économique',

  'lookbook.no_products': 'Aucun produit dans la présentation',
  'lookbook.no_products_hint': 'Sélectionnez des produits dans le catalogue, puis lancez le Lookbook.',
  'lookbook.back_to_catalogue': 'Retour au catalogue',
  'lookbook.back': 'Retour',
  'lookbook.counter': '{{current}} sur {{total}}',
  'lookbook.portfolios': 'Portfolios',
  'lookbook.customer_pdf': 'PDF Client ({{count}})',
  'lookbook.no_image': 'Pas d\'image',
  'lookbook.in_selection': 'Dans ma sélection',
  'lookbook.add_to_selection': 'Ajouter à ma sélection',
  'lookbook.my_portfolios': 'Mes Portfolios',
  'lookbook.portfolio_name_placeholder': 'Nom du portfolio…',
  'lookbook.no_portfolios': 'Aucun portfolio enregistré',

  // ── Visite guidée ─────────────────────────────────────────────────────
  'tour.btn.next': 'Suivant',
  'tour.btn.back': 'Précédent',
  'tour.btn.skip': 'Passer',
  'tour.btn.last': 'Compris',
  'tour.btn.close': 'Fermer',
  'tour.welcome.title': 'Bienvenue dans le catalogue produits',
  'tour.welcome.body': 'Petit tour de ce que vous pouvez faire ici. Environ une minute — vous pourrez le revoir à tout moment via le bouton « ? ».',
  'tour.lang.title': 'Choisissez votre langue',
  'tour.lang.body': 'Basculez toute l’application entre l’anglais, le français et l’espagnol. Les descriptions et les PDF suivent votre choix.',
  'tour.search.title': 'Rechercher des produits',
  'tour.search.body': 'Trouvez des produits par code, description ou catégorie. Les résultats se filtrent au fur et à mesure.',
  'tour.live.title': 'Produits actifs uniquement',
  'tour.live.body': 'Laissez activé pour n’afficher que les produits disponibles. Désactivez pour tout voir.',
  'tour.filters.title': 'Filtrer par régime & allergènes',
  'tour.filters.body': 'Touchez une puce une fois pour n’afficher que les produits correspondants, à nouveau pour les exclure. Idéal pour les demandes diététiques.',
  'tour.toggles.title': 'Afficher plus de détails',
  'tour.toggles.body': 'Activez ces options pour ajouter les ingrédients, les prix, la durée de vie décongelée et les consignes de manipulation à l’aperçu, au PDF et au Lookbook.',
  'tour.grid.title': 'Parcourir & sélectionner',
  'tour.grid.body': 'Cliquez sur une ligne pour prévisualiser un produit. Cochez la case pour l’ajouter à votre sélection. Double-cliquez sur une cellule pour modifier le texte.',
  'tour.selection.title': 'Votre sélection',
  'tour.selection.body': 'Indique le nombre de produits sélectionnés. Cliquez pour les réorganiser, les retirer ou les vérifier avant l’export.',
  'tour.pdf.title': 'Exporter un PDF',
  'tour.pdf.body': 'Générez un PDF catalogue prêt à imprimer de vos produits sélectionnés, dans la langue actuelle.',
  'tour.lookbook.title': 'Ouvrir le Lookbook',
  'tour.lookbook.body': 'Lancez une présentation plein écran et illustrée de votre sélection — idéale pour les clients.',
  'tour.preview.title': 'Aperçu & édition',
  'tour.preview.body': 'Le panneau d’aperçu affiche tout le détail produit. En ligne, vous pouvez y modifier le titre et la description marketing.',
  'tour.sync.title': 'Synchroniser',
  'tour.sync.body': 'Actualisez les données et les photos. L’application fonctionne aussi hors ligne avec les dernières données synchronisées.',
  'tour.help.title': 'Revoir la visite à tout moment',
  'tour.help.body': 'Un doute sur une fonction ? Cliquez sur ce bouton « ? » pour relancer la visite.',
  'tour.done.title': 'Tout est prêt',
  'tour.done.body': 'Voilà l’essentiel. Explorez à votre rythme — et relancez la visite depuis « ? » quand vous voulez.',
  'tour.lb.welcome.title': 'Bienvenue dans le Lookbook',
  'tour.lb.welcome.body': 'Une présentation plein écran de vos produits sélectionnés. Voici comment l’utiliser.',
  'tour.lb.nav.title': 'Naviguer entre les produits',
  'tour.lb.nav.body': 'Utilisez ces flèches — ou les touches ← → du clavier — pour parcourir votre sélection.',
  'tour.lb.add.title': 'Construire une sélection client',
  'tour.lb.add.body': 'Ajoutez les produits qui plaisent au client à « ma sélection », puis exportez-les en PDF personnalisé.',
  'tour.lb.portfolios.title': 'Portfolios',
  'tour.lb.portfolios.body': 'Enregistrez une sélection comme portfolio nommé pour la rouvrir plus tard — pratique par client ou par saison.',
  'tour.lb.pdf.title': 'PDF Client',
  'tour.lb.pdf.body': 'Exportez vos produits choisis en PDF soigné et personnalisé à envoyer au client.',
  'tour.lb.done.title': 'Profitez du Lookbook',
  'tour.lb.done.body': 'C’est tout — présentez, sélectionnez et exportez en toute simplicité.',
};

const es: typeof en = {
  'login.title': 'Catálogo de Productos',
  'login.subtitle': 'Inicie sesión con sus credenciales de Trac-IT',
  'login.username': 'Usuario',
  'login.password': 'Contraseña',
  'login.signin': 'Iniciar sesión',
  'login.error': 'Error de inicio de sesión. Verifique sus credenciales.',

  'header.menu': 'Menú',
  'header.syncing': 'Sincronizando…',
  'header.synced': 'Sincronizado a las {{time}}',
  'header.not_synced': 'No sincronizado',
  'header.sync_tooltip': 'Sincronizar datos y actualizar fotos',
  'header.sync_offline': 'Sin conexión — no se puede sincronizar',
  'header.offline': 'Sin conexión',
  'header.settings': 'Configuración',
  'header.signout': 'Cerrar sesión',
  'install.button': 'Instalar app',
  'install.tooltip': 'Instalar este catálogo como aplicación de escritorio',
  'install.help_title': 'Instalar la aplicación',
  'install.help_intro': 'Si no apareció la instalación con un clic, añade la app en dos clics:',
  'install.help_edge_label': 'Microsoft Edge',
  'install.help_edge': 'Abre el menú ⋯ → Más herramientas → Aplicaciones → «Instalar Didier Catalogue». (O haz clic en el icono de instalación a la derecha de la barra de direcciones.)',
  'install.help_chrome_label': 'Google Chrome',
  'install.help_chrome': 'Haz clic en el icono de instalación a la derecha de la barra de direcciones y luego «Instalar». (O: menú ⋮ → Enviar, guardar y compartir → Instalar página como aplicación.)',
  'install.help_installed_note': '¿Ya está instalada? Ábrela desde el menú ⋯ → «Abrir en Didier Catalogue», o desde el menú Inicio de Windows.',
  'install.help_close': 'Entendido',

  'catalogue.search_placeholder': 'Buscar código / descripción / categoría…',
  'catalogue.live_only': 'Solo activos',
  'catalogue.select_all': 'Seleccionar todos los productos visibles',
  'catalogue.deselect_all': 'Deseleccionar todo',
  'catalogue.n_selected': '{{count}} seleccionado(s)',
  'catalogue.none_selected': 'Sin selección',
  'catalogue.pdf_button': 'PDF ({{count}})',
  'catalogue.generating': 'Generando…',
  'catalogue.lookbook_button': 'Abrir Lookbook ({{count}})',
  'catalogue.n_products': '{{count}} productos',
  'catalogue.suitability_label': 'Apto para:',
  'catalogue.allergens_label': 'Alérgenos:',
  'catalogue.clear': 'Limpiar',
  'catalogue.clear_all': 'Limpiar todo',
  'catalogue.no_prefix': 'Sin ',
  'catalogue.airline_mode': 'Modo aerolínea',
  'catalogue.show_ingredients': 'Mostrar ingredientes',
  'catalogue.show_prices': 'Mostrar precios',
  'catalogue.show_defrost_life': 'Mostrar vida útil descongelada',
  'catalogue.show_handling': 'Mostrar manipulación',
  'catalogue.filter_to_selected': 'Filtrar selección',
  'catalogue.show_only_selected': 'Mostrar solo productos seleccionados',
  'catalogue.show_all_products': 'Mostrar todos los productos',

  'drawer.n_selected_one': '{{count}} producto seleccionado',
  'drawer.n_selected_other': '{{count}} productos seleccionados',
  'drawer.no_selected': 'No hay productos seleccionados',
  'drawer.remove_all': 'Eliminar todo',
  'drawer.empty_hint': 'Marque productos en la tabla para agregarlos aquí.',
  'drawer.move_up': 'Subir',
  'drawer.move_down': 'Bajar',
  'drawer.remove': 'Eliminar de la selección',
  'drawer.generate_pdf': 'Generar PDF ({{count}})',
  'drawer.lookbook': 'Lookbook',

  'preview.case_size': 'Talla de caja: x{{n}}',
  'preview.weight': 'Peso: {{n}} g',
  'preview.weight_per_unit': 'Peso: {{n}} g por unidad',
  'preview.fp_category': 'Categoría PF:',
  'preview.web_category': 'Categoría Web:',
  'preview.price': 'Precio: £{{n}}',
  'preview.defrost_life': 'Vida útil descongelada: {{n}} días',
  'preview.handling': 'Manipulación:',
  'preview.suitable_for': 'Apto para',
  'preview.allergens': 'Alérgenos',
  'preview.ingredients': 'Ingredientes',
  'preview.allergen_decl': 'Declaración de alérgenos',
  'preview.contains': 'Contiene: ',
  'preview.no_marketing_desc': 'Sin descripción de marketing',
  'preview.personalized': 'Personalizado',
  'preview.reset_personalization': 'Restablecer al catálogo',
  'preview.save': 'Guardar',
  'preview.cancel': 'Cancelar',
  'preview.saving': 'Guardando…',
  'preview.tier': 'Clase',

  'pdfconfirm.title': 'Generar catálogo PDF',
  'pdfconfirm.and_more': '…y {{n}} más',
  'pdfconfirm.settings': 'Configuración:',
  'pdfconfirm.chip_airline': 'Modo aerolínea',
  'pdfconfirm.chip_prices': 'Mostrar precios',
  'pdfconfirm.chip_ingredients': 'Mostrar ingredientes',
  'pdfconfirm.chip_defrost': 'Vida útil descongelada',
  'pdfconfirm.chip_handling': 'Manipulación',
  'pdfconfirm.standard': 'Estándar (sin opciones)',
  'pdfconfirm.cancel': 'Cancelar',
  'pdfconfirm.generate': 'Generar PDF',

  'pdf.suitable_for': 'Apto para:',
  'pdf.allergens': 'Alérgenos:',
  'pdf.ingredients': 'Ingredientes:',
  'pdf.defrost_life': 'Vida útil descongelada:',
  'pdf.handling': 'Manipulación:',
  'pdf.case_size': 'Talla de caja:',
  'pdf.weight': 'Peso:',
  'pdf.price': 'Precio:',
  'pdf.per_unit': 'por unidad',
  'pdf.days': 'días',
  'pdf.product_catalogue': 'Catálogo de Productos',
  'pdf.print_button': 'Imprimir / Guardar PDF',
  'pdf.close_button': 'Cerrar',

  'allergen.Gluten': 'Gluten',
  'allergen.Egg': 'Huevos',
  'allergen.Nuts': 'Frutos de cáscara',
  'allergen.Soya': 'Soja',
  'allergen.Dairy': 'Leche',
  'allergen.Sesame': 'Sésamo',
  'allergen.Sulphites': 'Dióxido de azufre',
  'allergen.Fish': 'Pescado',
  'allergen.Celery': 'Apio',
  'allergen.Mustard': 'Mostaza',
  'allergen.Lupin': 'Altramuces',
  'allergen.Mollusc': 'Moluscos',

  'suitability.Halal': 'Halal',
  'suitability.Vegetarian': 'Vegetariano',
  'suitability.Vegan': 'Vegano',

  'tier.business': 'Business',
  'tier.first': 'Primera clase',
  'tier.economy': 'Económico',

  'lookbook.no_products': 'No hay productos en la presentación',
  'lookbook.no_products_hint': 'Seleccione productos en el catálogo y luego abra el Lookbook.',
  'lookbook.back_to_catalogue': 'Volver al catálogo',
  'lookbook.back': 'Volver',
  'lookbook.counter': '{{current}} de {{total}}',
  'lookbook.portfolios': 'Portfolios',
  'lookbook.customer_pdf': 'PDF Cliente ({{count}})',
  'lookbook.no_image': 'Sin imagen',
  'lookbook.in_selection': 'En mi selección',
  'lookbook.add_to_selection': 'Añadir a mi selección',
  'lookbook.my_portfolios': 'Mis Portfolios',
  'lookbook.portfolio_name_placeholder': 'Nombre del portfolio…',
  'lookbook.no_portfolios': 'No hay portfolios guardados',

  // ── Visita guiada ─────────────────────────────────────────────────────
  'tour.btn.next': 'Siguiente',
  'tour.btn.back': 'Atrás',
  'tour.btn.skip': 'Omitir',
  'tour.btn.last': 'Entendido',
  'tour.btn.close': 'Cerrar',
  'tour.welcome.title': 'Bienvenido al catálogo de productos',
  'tour.welcome.body': 'Un recorrido rápido por lo que puede hacer aquí. Dura un minuto — puede repetirlo cuando quiera con el botón «?».',
  'tour.lang.title': 'Elija su idioma',
  'tour.lang.body': 'Cambie toda la aplicación entre inglés, francés y español. Las descripciones y los PDF siguen su elección.',
  'tour.search.title': 'Buscar productos',
  'tour.search.body': 'Encuentre productos por código, descripción o categoría. Los resultados se filtran mientras escribe.',
  'tour.live.title': 'Solo productos activos',
  'tour.live.body': 'Déjelo activado para mostrar solo los productos disponibles. Desactívelo para verlo todo.',
  'tour.filters.title': 'Filtrar por idoneidad y alérgenos',
  'tour.filters.body': 'Toque una etiqueta una vez para mostrar solo los productos coincidentes, de nuevo para excluirlos. Ideal para peticiones dietéticas.',
  'tour.toggles.title': 'Mostrar más detalle',
  'tour.toggles.body': 'Active estas opciones para añadir ingredientes, precios, vida útil descongelada e instrucciones de manipulación a la vista previa, el PDF y el Lookbook.',
  'tour.grid.title': 'Explorar y seleccionar',
  'tour.grid.body': 'Haga clic en una fila para previsualizar un producto. Marque la casilla para añadirlo a su selección. Haga doble clic en una celda para editar el texto.',
  'tour.selection.title': 'Su selección',
  'tour.selection.body': 'Muestra cuántos productos ha seleccionado. Haga clic para reordenarlos, quitarlos o revisarlos antes de exportar.',
  'tour.pdf.title': 'Exportar un PDF',
  'tour.pdf.body': 'Genere un PDF del catálogo listo para imprimir con sus productos seleccionados, en el idioma actual.',
  'tour.lookbook.title': 'Abrir el Lookbook',
  'tour.lookbook.body': 'Inicie una presentación a pantalla completa con fotos de su selección — ideal para mostrar a los clientes.',
  'tour.preview.title': 'Vista previa y edición',
  'tour.preview.body': 'El panel de vista previa muestra todo el detalle del producto. En línea puede editar aquí el título y la descripción de marketing.',
  'tour.sync.title': 'Sincronizar',
  'tour.sync.body': 'Actualice los datos y las fotos. La aplicación también funciona sin conexión con los últimos datos sincronizados.',
  'tour.help.title': 'Repita la visita cuando quiera',
  'tour.help.body': '¿Olvidó cómo funciona algo? Haga clic en este botón «?» para repetir la visita.',
  'tour.done.title': 'Todo listo',
  'tour.done.body': 'Eso es lo esencial. Explore a su ritmo — y repita la visita desde «?» cuando lo necesite.',
  'tour.lb.welcome.title': 'Bienvenido al Lookbook',
  'tour.lb.welcome.body': 'Una presentación a pantalla completa de sus productos seleccionados. Así se usa.',
  'tour.lb.nav.title': 'Moverse entre productos',
  'tour.lb.nav.body': 'Use estas flechas — o las teclas ← → del teclado — para recorrer su selección.',
  'tour.lb.add.title': 'Crear una selección de cliente',
  'tour.lb.add.body': 'Añada los productos que le gustan al cliente a «mi selección» y expórtelos como un PDF personalizado.',
  'tour.lb.portfolios.title': 'Portfolios',
  'tour.lb.portfolios.body': 'Guarde una selección como portfolio con nombre para reabrirla más tarde — útil por cliente o por temporada.',
  'tour.lb.pdf.title': 'PDF de cliente',
  'tour.lb.pdf.body': 'Exporte los productos elegidos como un PDF limpio y con su marca para enviar al cliente.',
  'tour.lb.done.title': 'Disfrute del Lookbook',
  'tour.lb.done.body': 'Eso es todo — presente, seleccione y exporte con facilidad.',
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    es: { translation: es },
  },
  lng: (localStorage.getItem('catalogue-lang') as SupportedLang | null) ?? 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

export default i18n;
